import type { ReactElement, ReactNode } from 'react';

import classNames from 'classnames';

import {
  attachComponentData,
  getComponentData,
  useElementFilter,
} from '@backstage/core-plugin-api';

import { Flex, Text } from '@spotify-internal/encore-web';
import { NarrowBox, PageTitle, Skeleton } from '@spotify/confidence-core-react';

import type { PageContentProps } from './Content/types';
import { HeaderCreateButton } from './HeaderCreateButton';

import styles from './Header.module.css';

export type { PageHeaderCreateButtonProps } from './HeaderCreateButton';

export const PAGE_HEADER_KEY = 'page.header';
const PAGE_HEADER_PRIMARY_ACTIONS_KEY = 'page.header.primaryActions';
const PAGE_HEADER_SECONDARY_ACTIONS_KEY = 'page.header.secondaryActions';

export type PageHeaderProps = { children?: ReactNode };
export type PageHeaderActionProps = { children?: ReactNode };

type HeaderChildKind = 'primaryActions' | 'secondaryActions' | 'unwrapped';

type PageHeaderActionSlot = {
  content: ReactNode;
};

export type PageHeaderActions = {
  primaryActions?: PageHeaderActionSlot;
  secondaryActions?: PageHeaderActionSlot;
};

const HeaderSlot = (_props: PageHeaderProps): null => null;

const HeaderPrimaryActionsSlot = (_props: PageHeaderActionProps): null => null;

const HeaderSecondaryActionsSlot = (_props: PageHeaderActionProps): null =>
  null;

attachComponentData(HeaderSlot, PAGE_HEADER_KEY, true);
attachComponentData(
  HeaderPrimaryActionsSlot,
  PAGE_HEADER_PRIMARY_ACTIONS_KEY,
  true,
);
attachComponentData(
  HeaderSecondaryActionsSlot,
  PAGE_HEADER_SECONDARY_ACTIONS_KEY,
  true,
);

const getHeaderChildKind = (element: ReactElement): HeaderChildKind => {
  if (getComponentData<boolean>(element, PAGE_HEADER_PRIMARY_ACTIONS_KEY)) {
    return 'primaryActions';
  }
  if (getComponentData<boolean>(element, PAGE_HEADER_SECONDARY_ACTIONS_KEY)) {
    return 'secondaryActions';
  }
  return 'unwrapped';
};

export const usePageHeaderActions = (children: ReactNode): PageHeaderActions =>
  useElementFilter(children, elements => {
    let primaryActions: PageHeaderActionSlot | undefined;
    let secondaryActions: PageHeaderActionSlot | undefined;

    elements.getElements<PageHeaderActionProps>().forEach(element => {
      const kind = getHeaderChildKind(element);
      if (process.env.NODE_ENV === 'development' && kind === 'unwrapped') {
        throw new Error(
          '<Page.Header> only accepts Page.Header.PrimaryActions and ' +
            'Page.Header.SecondaryActions children.',
        );
      }
      if (kind === 'primaryActions') {
        if (process.env.NODE_ENV === 'development' && primaryActions) {
          throw new Error(
            '<Page.Header> accepts only one Page.Header.PrimaryActions child.',
          );
        }
        primaryActions = { content: element.props.children };
      }
      if (kind === 'secondaryActions') {
        if (process.env.NODE_ENV === 'development' && secondaryActions) {
          throw new Error(
            '<Page.Header> accepts only one Page.Header.SecondaryActions child.',
          );
        }
        secondaryActions = { content: element.props.children };
      }
    });

    return { primaryActions, secondaryActions };
  });

export const Header = Object.assign(HeaderSlot, {
  PrimaryActions: HeaderPrimaryActionsSlot,
  SecondaryActions: HeaderSecondaryActionsSlot,
  CreateButton: HeaderCreateButton,
});

type HeaderLayoutProps = Pick<
  PageContentProps,
  'enableMargin' | 'isLoading' | 'narrow' | 'stickyHeader' | 'subheader'
> & {
  title: ReactNode;
  pageTitle: string;
  breadcrumbs: ReactNode;
  hasBreadcrumbs: boolean;
  primaryActions?: ReactNode;
  secondaryActions?: ReactNode;
};

export const HeaderLayout = ({
  title,
  pageTitle,
  breadcrumbs,
  hasBreadcrumbs,
  primaryActions,
  secondaryActions,
  enableMargin,
  narrow,
  isLoading,
  stickyHeader,
  subheader,
}: HeaderLayoutProps) => {
  const showBreadcrumbs = hasBreadcrumbs && !isLoading;
  const narrowWidth = typeof narrow === 'string' ? narrow : undefined;
  const headerContent = (
    <div
      className={classNames(styles.content, {
        [styles.withBreadcrumbs]: showBreadcrumbs,
        [styles.withSubheader]: Boolean(subheader),
      })}
    >
      {showBreadcrumbs && (
        <div className={styles.breadcrumbs}>{breadcrumbs}</div>
      )}
      {secondaryActions && (
        <Flex
          className={styles.secondaryActions}
          alignItems="center"
          justifyContent="end"
          flexShrink={0}
          gap="tighter-2"
        >
          {secondaryActions}
        </Flex>
      )}
      <Text as="h1" variant="titleSmall" className={styles.title} tabIndex={-1}>
        {isLoading ? <Skeleton variant="text" width={300} /> : title}
      </Text>
      {primaryActions && (
        <Flex
          className={styles.primaryActions}
          alignItems="center"
          justifyContent="end"
          flexShrink={0}
          gap="tighter-2"
        >
          {primaryActions}
        </Flex>
      )}
      {subheader && <div className={styles.subheader}>{subheader}</div>}
    </div>
  );

  return (
    <>
      <PageTitle title={pageTitle} />
      <header
        className={classNames(styles.root, {
          [styles.withMargin]: enableMargin,
          [styles.sticky]: stickyHeader,
        })}
      >
        <NarrowBox width={narrow ? narrowWidth : 'xl'}>
          {headerContent}
        </NarrowBox>
      </header>
    </>
  );
};
